import { Badge } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FormModal } from '../../../../components/FormModal'
import { getApiErrorMessage, useAppToast } from '../../../../helpers'
import { CoursesService } from '../../../../services/CoursesService/CoursesService'
import { ISelectCourse } from '../../../../services/CoursesService/interfaces'
import { EventSeriesService, IEditionFields, IEventSeriesFields } from '../../../../services/Event/EventSeriesService'
import { EventService } from '../../../../services/Event/EventService'
import {
  EventComplete,
  EventFormat,
  EventSeriesComplete,
  EventType,
  SeriesOverrideGroup,
  TicketType
} from '../../../../services/Event/types'
import { TeacherService } from '../../../../services/Teacher/TeacherService'
import { SelectTeacher } from '../../../../services/Teacher/types'
import { CaucaoInfoModal } from '../CaucaoInfoModal'
import { templateFromApi } from '../establishments/establishmentForm'
import { validateFaqs } from '../FaqEditor'
import { allowsHelpForm, HELP_FORM_MIN_TICKET_PRICE_LABEL, HELP_FORM_RULE_TEXT } from '../helpForm'
import { automaticSlug, editionNumbers } from './editionNumbering'
import { ContentTab } from './EventSeriesFormTabs/ContentTab'
import { EditionsTab } from './EventSeriesFormTabs/EditionsTab'
import { EngagementTab } from './EventSeriesFormTabs/EngagementTab'
import { EstablishmentsTab } from './EventSeriesFormTabs/EstablishmentsTab'
import { EditionForm, seedGroupFrom, SeriesForm, TemplateForm } from './EventSeriesFormTabs/eventSeriesFormShared'
import { FaqTab } from './EventSeriesFormTabs/FaqTab'
import { GeneralTab } from './EventSeriesFormTabs/GeneralTab'
import { LocationTab } from './EventSeriesFormTabs/LocationTab'
import { MarketingTab } from './EventSeriesFormTabs/MarketingTab'
import { TicketsTab } from './EventSeriesFormTabs/TicketsTab'
import {
  countErrorsPerTab,
  firstTabWithErrors,
  SERIES_TAB_DEFS,
  unmappedErrorMessages
} from './EventSeriesFormTabs/types'
import { GeneralValue, needsLocation } from './GeneralFieldsEditor'

interface IProps {
  isOpen: boolean
  onClose: () => void
  series?: EventSeriesComplete
  /**
   * When set, the form opens in "create series from a standalone event" mode:
   * general fields are pre-filled from the event, the editions/tickets sections
   * are hidden, and on save the new series is created and the event is attached
   * to it (keeping all of its own data — tickets, participants, images).
   */
  sourceEvent?: EventComplete
  /**
   * Jumps straight to the Edições tab with this edition's card expanded and scrolled
   * into view, instead of the default Geral tab with everything collapsed — used by
   * the "Editar edição" menu item on the "Ver edições" screen.
   */
  focusEditionId?: number
}

const baseGeneral = (): GeneralValue => ({
  format: '',
  types: [],
  thumb: null,
  thumbPreview: null,
  thumb_wide: null,
  thumbWidePreview: null,
  video: null,
  videoPreview: null,
  remove_video: false,
  copy: '',
  target_audience: '',
  topics: '',
  min_installment_value: null,
  use_term: false,
  term: null,
  use_testimonial: false,
  testimonial_title: null,
  testimonials: [],
  active_campaign_lead_lists: [],
  active_campaign_buyer_lists: [],
  use_help_form: false,
  zipcode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  latitude: null,
  longitude: null,
  place_id: null,
  place_name: null
})

const baseEdition = (): EditionForm => ({
  ...baseGeneral(),
  _key: crypto.randomUUID(),
  name: '',
  slug: '',
  visible: true,
  use_nfc: false,
  date_start: '',
  is_multiday: false,
  date_end: '',
  time_start: '',
  time_end: '',
  professionals: [],
  overrides: []
})

const baseForm = (): SeriesForm => ({
  ...baseGeneral(),
  name: '',
  slug: '',
  course_ids: [],
  faqs: [],
  editions: [baseEdition()],
  ticket_templates: [],
  establishment_templates: []
})

/** Diz quais edições travam o switch "Tire suas dúvidas" da série e o que fazer a respeito. */
const helpFormBlockedHint = (blocked: EditionForm[]): string => {
  const plural = blocked.length > 1
  const names = blocked.map((ed) => `“${ed.name || 'sem nome'}”`).join(', ')

  return (
    `${HELP_FORM_RULE_TEXT} ${plural ? 'As edições' : 'A edição'} ${names} não ${plural ? 'têm' : 'tem'} ` +
    `ingresso pago de ${HELP_FORM_MIN_TICKET_PRICE_LABEL} ou mais. Cadastre um ingresso nesse valor ou ` +
    `personalize “Tire suas dúvidas” ${plural ? 'nessas edições' : 'nessa edição'}.`
  )
}

const generalFromEntity = (e: {
  format: EventFormat
  types?: { id: number }[]
  thumb?: string | null
  thumb_wide?: string | null
  video_url?: string | null
  copy?: string | null
  target_audience?: string | null
  topics?: string | null
  min_installment_value?: number | null
  use_term?: boolean
  term?: string | null
  use_testimonial?: boolean
  testimonial_title?: string | null
  testimonial_urls?: string[] | null
  active_campaign_lead_lists?: string[] | null
  active_campaign_buyer_lists?: string[] | null
  use_help_form?: boolean
  zipcode?: string | null
  street?: string | null
  number?: string | null
  complement?: string | null
  neighborhood?: string | null
  city?: string | null
  state?: string | null
  latitude?: number | null
  longitude?: number | null
  place_id?: string | null
  place_name?: string | null
}): GeneralValue => ({
  format: e.format,
  types: e.types?.map((t) => t.id) ?? [],
  thumb: null,
  thumbPreview: e.thumb ?? null,
  thumb_wide: null,
  thumbWidePreview: e.thumb_wide ?? null,
  video: null,
  videoPreview: e.video_url ?? null,
  remove_video: false,
  copy: e.copy ?? '',
  target_audience: e.target_audience ?? '',
  topics: e.topics ?? '',
  min_installment_value: e.min_installment_value ?? null,
  use_term: e.use_term ?? false,
  term: e.term ?? null,
  use_testimonial: e.use_testimonial ?? false,
  testimonial_title: e.testimonial_title ?? null,
  testimonials:
    e.testimonial_urls && e.testimonial_urls.length > 0
      ? e.testimonial_urls.map((url) => ({ _id: crypto.randomUUID(), file: null, existingUrl: url }))
      : [],
  active_campaign_lead_lists: e.active_campaign_lead_lists ?? [],
  active_campaign_buyer_lists: e.active_campaign_buyer_lists ?? [],
  use_help_form: e.use_help_form ?? false,
  zipcode: e.zipcode ?? '',
  street: e.street ?? '',
  number: e.number ?? '',
  complement: e.complement ?? '',
  neighborhood: e.neighborhood ?? '',
  city: e.city ?? '',
  state: e.state ?? '',
  latitude: e.latitude ?? null,
  longitude: e.longitude ?? null,
  place_id: e.place_id ?? null,
  place_name: e.place_name ?? null
})

const buildFromSeries = (series: EventSeriesComplete): SeriesForm => {
  const editions: EditionForm[] = series.editions.map((edition) => ({
    ...generalFromEntity(edition),
    _key: crypto.randomUUID(),
    id: edition.id,
    name: edition.name,
    slug: edition.slug,
    publishedSlug: edition.slug,
    savedNumber: edition.edition_number ?? undefined,
    customSlugDraft: edition.overrides?.includes('slug') ? edition.slug : undefined,
    visible: edition.visible,
    use_nfc: edition.use_nfc ?? false,
    date_start: edition.date_start ? edition.date_start.slice(0, 10) : '',
    is_multiday: edition.is_multiday,
    date_end: edition.date_end ? edition.date_end.slice(0, 10) : '',
    time_start: edition.time_start ?? '',
    time_end: edition.time_end ?? '',
    professionals:
      edition.professionals?.map((p) => ({
        _id: crypto.randomUUID(),
        teacher_id: p.teacher_id ?? null,
        title: p.title,
        name: p.name,
        bio: p.bio || '',
        thumb: null,
        thumbPreview: p.thumb ?? null
      })) ?? [],
    overrides: edition.overrides ?? []
  }))

  const editionIdToIndex = new Map<number, number>()
  editions.forEach((ed, i) => {
    if (ed.id) editionIdToIndex.set(ed.id, i)
  })

  const ticket_templates: TemplateForm[] = (series.ticket_templates ?? []).map((tpl) => ({
    id: tpl.id,
    type: tpl.type,
    title: tpl.title,
    quantity: tpl.quantity,
    price: Number(tpl.price),
    sales_start: tpl.sales_start ? tpl.sales_start.slice(0, 16) : '',
    sales_end: tpl.sales_end ? tpl.sales_end.slice(0, 16) : '',
    max_per_purchase: tpl.max_per_purchase,
    description: tpl.description || '',
    visible: tpl.visible,
    fee_exempt: tpl.fee_exempt ?? false,
    is_child_ticket: tpl.is_child_ticket ?? false,
    sell_at_door: tpl.sell_at_door ?? false,
    door_price: tpl.door_price != null ? Number(tpl.door_price) : null,
    edition_indexes: (tpl.edition_ids ?? [])
      .map((id) => editionIdToIndex.get(id))
      .filter((i): i is number => i !== undefined)
  }))

  return {
    ...generalFromEntity(series),
    name: series.name,
    slug: series.slug ?? '',
    course_ids: series.courses?.map((c) => c.id) ?? [],
    faqs:
      series.faqs?.map((f) => ({
        _id: crypto.randomUUID(),
        id: f.id,
        question: f.question,
        answer: f.answer
      })) ?? [],
    editions,
    ticket_templates,
    establishment_templates: (series.establishment_templates ?? []).map((tpl) =>
      templateFromApi(
        tpl,
        editions.map((ed) => ed.id)
      )
    )
  }
}

/**
 * Seed a new series from a standalone event: its general fields become the series
 * defaults. No editions/tickets are created here — the event itself is attached
 * after the series is saved (keeping all of its own data).
 */
const buildFromEvent = (event: EventComplete): SeriesForm => ({
  ...generalFromEntity(event),
  name: event.name,
  // A série herda os cursos que o evento já tinha; ao ser anexado como edição ele
  // passa a receber os cursos pela série.
  course_ids: event.courses?.map((c) => c.id) ?? [],
  // Left empty on purpose: the event keeps its own URL when it is attached, so reusing
  // it as the base would make the next edition derive the very same URL (a collision) —
  // and the source event may itself be an edition, whose slug is already a derived one.
  slug: '',
  // Start with no series FAQs: the event keeps its own FAQs and is attached as an
  // edition, so copying them here would show each question twice (additive rule).
  faqs: [],
  editions: [],
  ticket_templates: [],
  // O evento traz os estabelecimentos dele ao ser anexado: a API os converte em modelos.
  establishment_templates: []
})

export function EventSeriesFormModal({ isOpen, onClose, series, sourceEvent, focusEditionId }: IProps) {
  const seriesService = new EventSeriesService()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  // A edição fica aberta depois de salvar (ABRA-399) e passa a valer a série que a API devolveu:
  // os ids das linhas criadas agora e as URLs que as edições ganharam vêm dela.
  const [savedSeries, setSavedSeries] = useState(series)
  const [form, setForm] = useState<SeriesForm>(() =>
    series ? buildFromSeries(series) : sourceEvent ? buildFromEvent(sourceEvent) : baseForm()
  )
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})
  // The form is split across tabs, so a failed save has to point at the offending one.
  // Lazy-initialized (not reset in an effect): the modal always remounts fresh — via a
  // bumped `key` — on every open (EventPage.tsx and EventEditionsModal.tsx both do this),
  // so `series`/`sourceEvent`/`focusEditionId` never actually change under a mounted
  // instance. An effect mirroring these props would only ever fire once, redundantly
  // re-deriving the same form (a second, wasted set of `_key` UUIDs) after first paint —
  // and until it did, `tabIndex` would sit on Geral for a frame even when opened via
  // "Editar edição", producing a visible flash to the intended Edições tab.
  const [tabIndex, setTabIndex] = useState(() =>
    focusEditionId ? SERIES_TAB_DEFS.findIndex((tab) => tab.key === 'editions') : 0
  )
  const [slugChangeOpen, setSlugChangeOpen] = useState(false)
  const [caucaoInfoOpen, setCaucaoInfoOpen] = useState(false)
  const [eventTypes, setEventTypes] = useState<EventType[]>([])
  const [teachers, setTeachers] = useState<SelectTeacher[]>([])
  const [courses, setCourses] = useState<ISelectCourse[]>([])
  const [activeCampaignLists, setActiveCampaignLists] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    const eventService = new EventService()
    eventService.getTypes().then(setEventTypes)
    eventService
      .getActiveCampaignLists()
      .then(setActiveCampaignLists)
      .catch(() => toast.error('Não foi possível carregar as listas do Active Campaign'))
    new TeacherService().listForSelect().then(setTeachers)
    // Só presenciais e online ao vivo: curso gravado não tem página com carrossel
    // de eventos.
    new CoursesService()
      .listForSelect(true, true, false)
      .then(setCourses)
      .catch(() => toast.error('Não foi possível carregar a lista de cursos'))
  }, [])

  // `_key` is regenerated on every mount (see buildFromSeries), so `id` — stable, from
  // the API — is the only way to point at "this edition" from outside this component.
  const focusEditionKey =
    focusEditionId != null ? form.editions.find((ed) => ed.id === focusEditionId)?._key : undefined

  const numbers = useMemo(() => editionNumbers(form.editions), [form.editions])

  const editionNumber = (edition: EditionForm) => numbers.get(edition._key) ?? 1

  /** URL the edition gets from the series when it does not override the `slug` group. */
  const derivedSlug = (edition: EditionForm) => automaticSlug(form.slug, editionNumber(edition))

  /** URL the edition will end up with once saved: its own when overridden, the derived one otherwise. */
  const targetSlug = (edition: EditionForm) => {
    if (edition.overrides.includes('slug')) return edition.slug

    // An automatic edition that hasn't moved and whose base URL is unchanged keeps exactly
    // what the API stored — which may carry a `-2` dedupe suffix the pattern can't reproduce.
    // Re-deriving here would invent a change that never happens (a warning that never clears).
    if (edition.publishedSlug && edition.savedNumber === editionNumber(edition) && form.slug === savedSeries?.slug) {
      return edition.publishedSlug
    }

    return derivedSlug(edition)
  }

  /**
   * The API rewrites the URL on save and keeps no redirect from the old one, so any edition
   * whose target URL no longer matches what is published today has its public link dying.
   */
  const willChangeSlug = (edition: EditionForm) =>
    !!edition.publishedSlug && !!targetSlug(edition) && targetSlug(edition) !== edition.publishedSlug

  const editionsChangingSlug = form.editions.filter(willChangeSlug)

  /**
   * Cada edição é um evento, então a regra do "Tire suas dúvidas" (ver ../helpForm) vale
   * por edição: valem os ingressos dos templates atribuídos a ela.
   */
  const editionAllowsHelpForm = (index: number) =>
    allowsHelpForm(form.ticket_templates.filter((tpl) => tpl.edition_indexes.includes(index)))

  /**
   * O switch da série é o padrão de toda edição que não personaliza `help_form`, então só
   * fica liberado quando todas elas têm o ingresso pago exigido. Série sem edição (ou com
   * todas personalizando o grupo) não impõe nada a ninguém — fica livre.
   */
  const editionsBlockingHelpForm = form.editions.filter(
    (ed, i) => !ed.overrides.includes('help_form') && !editionAllowsHelpForm(i)
  )

  const seriesHelpFormAllowed = editionsBlockingHelpForm.length === 0

  const seriesHelpFormBlockedHint = helpFormBlockedHint(editionsBlockingHelpForm)

  // "Edições"/"Ingressos"/"Produtos e Serviços" don't exist yet in sourceEvent mode (mirrors the old
  // `{!sourceEvent && (...)}` block), and "Local" only applies to in-person/hybrid series.
  const hiddenTabKeys = new Set<string>([
    ...(sourceEvent ? ['editions', 'tickets', 'establishments'] : []),
    ...(needsLocation(form.format) ? [] : ['location'])
  ])
  const visibleTabDefs = SERIES_TAB_DEFS.filter((tab) => !hiddenTabKeys.has(tab.key))
  const errorCounts = countErrorsPerTab(visibleTabDefs, fieldErrors)

  const patchSeries = (patch: Partial<SeriesForm>) => setForm((prev) => ({ ...prev, ...patch }))

  // Stable (empty deps): both are purely functional `setForm` updates with no closure
  // over component state, so they're safe to hand to a memoized EditionCard — it can
  // skip re-rendering unaffected cards without ever holding a stale one of these.
  const patchEdition = useCallback(
    (key: string, patch: Partial<EditionForm>) =>
      setForm((prev) => ({
        ...prev,
        editions: prev.editions.map((ed) => (ed._key === key ? { ...ed, ...patch } : ed))
      })),
    []
  )

  const addEdition = () => setForm((prev) => ({ ...prev, editions: [...prev.editions, baseEdition()] }))

  const removeEdition = useCallback((key: string) => {
    setForm((prev) => {
      const removedIndex = prev.editions.findIndex((ed) => ed._key === key)
      const editions = prev.editions.filter((ed) => ed._key !== key)
      // Keep ticket edition_indexes aligned after removing an edition.
      const realign = (indexes: number[]) =>
        indexes.filter((i) => i !== removedIndex).map((i) => (i > removedIndex ? i - 1 : i))
      const ticket_templates = prev.ticket_templates.map((tpl) => ({
        ...tpl,
        edition_indexes: realign(tpl.edition_indexes)
      }))
      // Os modelos de estabelecimento apontam para edições pelo mesmo índice (ABRA-317).
      const establishment_templates = prev.establishment_templates.map((tpl) => ({
        ...tpl,
        edition_indexes: realign(tpl.edition_indexes)
      }))
      return { ...prev, editions, ticket_templates, establishment_templates }
    })
    // Edition indices shift on removal, so drop the per-edition errors to avoid a
    // remaining edition inheriting another one's stale error (mirrors
    // removeTestimonialSlot in EventFormModal.tsx).
    setFieldErrors((prev) => {
      const next: Record<string, string[]> = {}
      Object.entries(prev).forEach(([errorKey, value]) => {
        if (errorKey.startsWith('editions.')) return
        next[errorKey] = value
      })
      return next
    })
  }, [])

  // Stable (empty deps): reads only `prev` (always fresh) via `seedGroupFrom`, a pure
  // function — never a stale `form` closure, so it's safe for a memoized EditionCard to
  // hold onto across renders it was skipped for.
  const toggleOverride = useCallback((edition: EditionForm, group: SeriesOverrideGroup, enabled: boolean) => {
    setForm((prev) => ({
      ...prev,
      editions: prev.editions.map((ed) => {
        if (ed._key !== edition._key) return ed
        if (!enabled) return { ...ed, overrides: ed.overrides.filter((g) => g !== group) }

        const editionNumber = editionNumbers(prev.editions).get(ed._key) ?? 1
        return { ...ed, overrides: [...ed.overrides, group], ...seedGroupFrom(prev, group, ed, editionNumber) }
      })
    }))
  }, [])

  const addTemplate = (type: TicketType) =>
    setForm((prev) => ({
      ...prev,
      ticket_templates: [
        ...prev.ticket_templates,
        {
          type,
          title: '',
          quantity: '',
          price: 0,
          sales_start: '',
          sales_end: '',
          max_per_purchase: '',
          description: '',
          visible: true,
          fee_exempt: false,
          is_child_ticket: false,
          sell_at_door: false,
          door_price: null,
          edition_indexes: prev.editions.map((_, i) => i)
        }
      ]
    }))

  const updateTemplate = (index: number, patch: Partial<TemplateForm>) =>
    setForm((prev) => ({
      ...prev,
      ticket_templates: prev.ticket_templates.map((tpl, i) => (i === index ? { ...tpl, ...patch } : tpl))
    }))

  const removeTemplate = (index: number) =>
    setForm((prev) => ({ ...prev, ticket_templates: prev.ticket_templates.filter((_, i) => i !== index) }))

  const handleSave = async () => {
    setSaving(true)
    setFieldErrors({})

    const faqErrors = validateFaqs(form.faqs)
    if (Object.keys(faqErrors).length > 0) {
      setFieldErrors(faqErrors)
      setSaving(false)
      toast.error('Erro de validação', 'Preencha a pergunta e a resposta em todas as perguntas frequentes.')
      return
    }

    // Uma edição que perdeu o ingresso caro (ou a série cuja edição nunca teve) volta com a
    // opção desligada, em vez de ser bloqueada pela API na hora de salvar.
    const editions: IEditionFields[] = form.editions.map((ed, i) => ({
      ...ed,
      use_help_form: editionAllowsHelpForm(i) && ed.use_help_form
    }))

    const payload: IEventSeriesFields = {
      name: form.name,
      slug: form.slug,
      format: form.format,
      thumb: form.thumb,
      thumb_wide: form.thumb_wide,
      video: form.video,
      videoPreview: form.videoPreview,
      remove_video: form.remove_video,
      copy: form.copy,
      target_audience: form.target_audience,
      topics: form.topics,
      min_installment_value: form.min_installment_value,
      use_term: form.use_term,
      term: form.term,
      use_testimonial: form.use_testimonial,
      testimonial_title: form.testimonial_title,
      testimonials: form.testimonials,
      active_campaign_lead_lists: form.active_campaign_lead_lists,
      active_campaign_buyer_lists: form.active_campaign_buyer_lists,
      use_help_form: seriesHelpFormAllowed && form.use_help_form,
      types: form.types,
      course_ids: form.course_ids,
      zipcode: form.zipcode,
      street: form.street,
      number: form.number,
      complement: form.complement,
      neighborhood: form.neighborhood,
      city: form.city,
      state: form.state,
      latitude: form.latitude,
      longitude: form.longitude,
      place_id: form.place_id,
      place_name: form.place_name,
      faqs: form.faqs,
      editions,
      ticket_templates: form.ticket_templates.map((tpl) => ({
        id: tpl.id,
        type: tpl.type,
        title: tpl.title,
        quantity: Number(tpl.quantity) || 0,
        price: tpl.price,
        sales_start: tpl.sales_start,
        sales_end: tpl.sales_end,
        max_per_purchase: Number(tpl.max_per_purchase) || 0,
        description: tpl.description,
        visible: tpl.visible,
        fee_exempt: tpl.fee_exempt,
        is_child_ticket: tpl.is_child_ticket,
        sell_at_door: tpl.sell_at_door,
        door_price: tpl.door_price,
        edition_indexes: tpl.edition_indexes
      })),
      establishment_templates: form.establishment_templates
    }

    const saveFunction = savedSeries ? seriesService.update(savedSeries.id, payload) : seriesService.create(payload)

    saveFunction
      .then(async (result) => {
        // Created from a standalone event: link it to the brand-new series,
        // preserving its tickets, participants and configuration.
        if (sourceEvent) {
          try {
            await seriesService.attachEvent(result.id, sourceEvent.id)
          } catch (attachErr) {
            // The series was created; only linking the event failed. Handle it
            // apart from the create error so we don't show "Erro ao criar série"
            // and close the modal to avoid recreating the series on retry.
            toast.warning(
              'Série criada, mas não foi possível vincular o evento',
              'A série foi criada com sucesso. Vincule o evento manualmente pela opção "Adicionar a uma série".'
            )
            onClose()
            return
          }
        }
        // A API aplica o resto da atualização e só preserva "Ingresso Infantil" nas edições
        // que já tiveram check-in com o ingresso — o aviso diz quais ficaram como estavam.
        const skippedEditions = result.skipped_child_ticket_editions ?? []
        if (skippedEditions.length > 0) {
          toast.warning(
            'Ingresso Infantil não foi alterado em algumas edições',
            `Já houve check-in com esse ingresso em: ${skippedEditions.join(', ')}.`
          )
        }
        toast.success(
          sourceEvent
            ? 'Série criada e evento vinculado com sucesso'
            : savedSeries
              ? 'Série atualizada com sucesso'
              : 'Série criada com sucesso'
        )
        if (!savedSeries) {
          onClose()
          return
        }
        // A edição fica aberta, na mesma aba (ABRA-399), com o formulário de volta da API: um
        // modelo ou uma edição criada agora só ganha id assim, e sem ele o próximo Salvar a criaria
        // de novo. Por isso, se a recarga falhar, o modal fecha. O `saving` só solta depois dela.
        try {
          const fresh = await seriesService.get(savedSeries.id)
          setSavedSeries(fresh)
          setForm(buildFromSeries(fresh))
        } catch (error) {
          toast.error('Série salva, mas não foi possível recarregar o formulário', error)
          onClose()
        }
      })
      .catch((err) => {
        if (err?.response?.status === 422 && err?.response?.data?.errors) {
          setFieldErrors(err.response.data.errors)
          const offendingTab = firstTabWithErrors(visibleTabDefs, err.response.data.errors)
          // -1 (no tab's field list matched) still jumps to Geral instead of leaving
          // the tab wherever it was — a forgotten field there is less confusing than
          // the toast pointing nowhere.
          setTabIndex(offendingTab >= 0 ? offendingTab : 0)
          // No tab has a field to show it on, so at least put the real message in
          // front of the user instead of a generic "corrija os erros" that points at
          // nothing.
          toast.error(
            'Erro de validação',
            offendingTab >= 0
              ? 'Corrija os erros indicados no formulário.'
              : unmappedErrorMessages(visibleTabDefs, err.response.data.errors).join(' ')
          )
          return
        }
        if (err?.response?.status === 422 && err?.response?.data?.message) {
          toast.warning('Operação não permitida', getApiErrorMessage(err))
          return
        }
        if (err?.response?.status === 400 && err?.response?.data?.errors) {
          setFieldErrors(err.response.data.errors)
          const offendingTab = firstTabWithErrors(visibleTabDefs, err.response.data.errors)
          // -1 (no tab's field list matched) still jumps to Geral instead of leaving
          // the tab wherever it was — a forgotten field there is less confusing than
          // the toast pointing nowhere.
          setTabIndex(offendingTab >= 0 ? offendingTab : 0)
          // No tab has a field to show it on, so at least put the real message in
          // front of the user instead of a generic "corrija os erros" that points at
          // nothing.
          toast.error(
            'Erro de validação',
            offendingTab >= 0
              ? 'Corrija os erros indicados no formulário.'
              : unmappedErrorMessages(visibleTabDefs, err.response.data.errors).join(' ')
          )
          return
        }
        toast.error(savedSeries ? 'Erro ao atualizar série' : 'Erro ao criar série')
      })
      .finally(() => setSaving(false))
  }

  /**
   * Client-side check of the required fields the form controls, so the irreversible URL
   * confirmation never opens before validation. Mirrors the `required` rules of
   * EventSeriesRequest; the backend 422 stays the source of truth for anything not covered.
   * Returns true when the form is clean; otherwise fills `fieldErrors` and toasts.
   */
  const validate = (): boolean => {
    const errors: Record<string, string[]> = { ...validateFaqs(form.faqs) }
    const slugPattern = /^[\w-]+$/

    if (!form.name.trim()) errors['name'] = ['O nome da série é obrigatório.']
    if (!form.slug.trim()) errors['slug'] = ['A URL da série é obrigatória.']
    else if (!slugPattern.test(form.slug))
      errors['slug'] = ['A URL deve conter apenas letras, números, hífens e underscores.']

    form.editions.forEach((ed, i) => {
      const p = `editions.${i}.`
      if (!ed.name?.trim()) errors[`${p}name`] = ['O nome da edição é obrigatório.']
      if (!ed.date_start) errors[`${p}date_start`] = ['A data da edição é obrigatória.']
      if (!ed.time_start) errors[`${p}time_start`] = ['O horário de início é obrigatório.']
      if (!ed.time_end) errors[`${p}time_end`] = ['O horário de término é obrigatório.']
      if (ed.is_multiday && !ed.date_end) errors[`${p}date_end`] = ['A data de término é obrigatória.']
      if (ed.overrides.includes('slug')) {
        if (!ed.slug?.trim()) errors[`${p}slug`] = ['A URL da edição é obrigatória.']
        else if (!slugPattern.test(ed.slug))
          errors[`${p}slug`] = ['A URL deve conter apenas letras, números, hífens e underscores.']
      }
    })

    form.ticket_templates.forEach((tpl, idx) => {
      const p = `ticket_templates.${idx}.`
      if (!tpl.title?.trim()) errors[`${p}title`] = ['O título do ingresso é obrigatório.']
      if (!(Number(tpl.quantity) > 0)) errors[`${p}quantity`] = ['Informe uma quantidade válida.']
      if (tpl.type === 'PAID' && Number.isNaN(Number(tpl.price)))
        errors[`${p}price`] = ['O preço é obrigatório para ingressos pagos.']
      if (!tpl.sales_start) errors[`${p}sales_start`] = ['A data de início das vendas é obrigatória.']
      if (!tpl.sales_end) errors[`${p}sales_end`] = ['A data de término das vendas é obrigatória.']
      if (!(Number(tpl.max_per_purchase) > 0)) errors[`${p}max_per_purchase`] = ['Informe um máximo por compra válido.']
      if (!tpl.edition_indexes?.length) errors[`${p}edition_indexes`] = ['Selecione ao menos uma edição.']
      if (tpl.type === 'PAID' && tpl.sell_at_door) {
        if (tpl.door_price === null) errors[`${p}door_price`] = ['Informe o preço na porta.']
        else if (tpl.door_price < 5) errors[`${p}door_price`] = ['O preço na porta deve ser de pelo menos R$ 5,00.']
      }
    })

    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      const offendingTab = firstTabWithErrors(visibleTabDefs, errors)
      setTabIndex(offendingTab >= 0 ? offendingTab : 0)
      toast.error('Erro de validação', 'Corrija os erros indicados no formulário.')
      return false
    }

    return true
  }

  const editionLabel = (ed: EditionForm) => ed.name?.trim() || 'Nova edição'

  const editionOptionLabel = (ed: EditionForm) => `Edição ${editionNumber(ed)} — ${editionLabel(ed)}`

  // Rótulo da aba com o contador de erros, pelo índice na lista visível.
  const tabLabel = (index: number) => (
    <>
      {visibleTabDefs[index]?.label}
      {errorCounts[index] > 0 && (
        <Badge ml={2} colorScheme="red">
          {errorCounts[index]}
        </Badge>
      )}
    </>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={
          savedSeries
            ? 'Editar Série de Eventos'
            : sourceEvent
              ? 'Criar Série a partir do Evento'
              : 'Cadastrar Série de Eventos'
        }
        onSubmit={() => {
          if (!validate()) return
          if (editionsChangingSlug.length > 0) setSlugChangeOpen(true)
          else handleSave()
        }}
        // Mesma razão do formulário de evento: um obrigatório vazio numa aba fechada travava o
        // Salvar em silêncio. A validação é o `validate()` acima e a API, que levam o erro à aba.
        noValidate
        size="editor"
        isLoading={saving}
        // Salvar a edição não fecha o modal (ABRA-399): sair é por aqui ou pelo X.
        cancelLabel={savedSeries ? 'Fechar' : undefined}
        tabIndex={Math.min(tabIndex, visibleTabDefs.length - 1)}
        onTabChange={setTabIndex}
        areTabsLazy
        tabs={[
          {
            label: tabLabel(0),
            content: (
              <GeneralTab
                form={form}
                patchSeries={patchSeries}
                fieldErrors={fieldErrors}
                saving={saving}
                sourceEvent={sourceEvent}
                eventTypes={eventTypes}
                courses={courses}
                activeCampaignLists={activeCampaignLists}
                editionsChangingSlug={editionsChangingSlug}
              />
            )
          },
          {
            label: tabLabel(1),
            content: (
              <ContentTab
                form={form}
                patchSeries={patchSeries}
                fieldErrors={fieldErrors}
                saving={saving}
                eventTypes={eventTypes}
                activeCampaignLists={activeCampaignLists}
              />
            )
          },
          {
            label: tabLabel(2),
            content: (
              <EngagementTab
                form={form}
                patchSeries={patchSeries}
                fieldErrors={fieldErrors}
                saving={saving}
                eventTypes={eventTypes}
                activeCampaignLists={activeCampaignLists}
                seriesHelpFormAllowed={seriesHelpFormAllowed}
                seriesHelpFormBlockedHint={seriesHelpFormBlockedHint}
              />
            )
          },
          {
            label: tabLabel(3),
            content: <FaqTab form={form} patchSeries={patchSeries} fieldErrors={fieldErrors} saving={saving} />
          },
          {
            label: tabLabel(4),
            content: (
              <MarketingTab
                form={form}
                patchSeries={patchSeries}
                fieldErrors={fieldErrors}
                saving={saving}
                eventTypes={eventTypes}
                activeCampaignLists={activeCampaignLists}
              />
            )
          },
          // Edições, Ingressos e Produtos e Serviços não existem quando a série nasce de um evento
          // pronto, e Local depende do formato. Como array, cada uma some sem deslocar as outras —
          // montadas à mão, duas delas dentro de um mesmo Fragment quebravam a indexação do Chakra.
          ...(!sourceEvent
            ? [
                {
                  label: tabLabel(5),
                  content: (
                    <EditionsTab
                      form={form}
                      series={savedSeries}
                      fieldErrors={fieldErrors}
                      saving={saving}
                      teachers={teachers}
                      eventTypes={eventTypes}
                      activeCampaignLists={activeCampaignLists}
                      addEdition={addEdition}
                      removeEdition={removeEdition}
                      patchEdition={patchEdition}
                      toggleOverride={toggleOverride}
                      editionNumber={editionNumber}
                      targetSlug={targetSlug}
                      willChangeSlug={willChangeSlug}
                      editionLabel={editionLabel}
                      editionAllowsHelpForm={editionAllowsHelpForm}
                      initialExpandedKey={focusEditionKey}
                    />
                  )
                },
                {
                  label: tabLabel(6),
                  content: (
                    <TicketsTab
                      form={form}
                      patchSeries={patchSeries}
                      fieldErrors={fieldErrors}
                      saving={saving}
                      addTemplate={addTemplate}
                      updateTemplate={updateTemplate}
                      removeTemplate={removeTemplate}
                      setCaucaoInfoOpen={setCaucaoInfoOpen}
                      editionNumber={editionNumber}
                      editionOptionLabel={editionOptionLabel}
                    />
                  )
                },
                {
                  label: tabLabel(7),
                  content: (
                    <EstablishmentsTab
                      form={form}
                      patchSeries={patchSeries}
                      fieldErrors={fieldErrors}
                      setFieldErrors={setFieldErrors}
                      saving={saving}
                      editionNumber={editionNumber}
                      editionOptionLabel={editionOptionLabel}
                    />
                  )
                }
              ]
            : []),
          ...(needsLocation(form.format)
            ? [
                {
                  label: tabLabel(sourceEvent ? 5 : 8),
                  content: (
                    <LocationTab
                      form={form}
                      patchSeries={patchSeries}
                      fieldErrors={fieldErrors}
                      saving={saving}
                      eventTypes={eventTypes}
                      activeCampaignLists={activeCampaignLists}
                    />
                  )
                }
              ]
            : [])
        ]}
      />

      {/* Salvar is always on screen while the warnings are not, so the URL rewrite gets a
      confirmation of its own — it has no undo (the API keeps no redirect). */}
      <ConfirmDialog
        isOpen={slugChangeOpen}
        onClose={() => setSlugChangeOpen(false)}
        onConfirm={() => {
          setSlugChangeOpen(false)
          handleSave()
        }}
        action="save"
        title="Alterar URLs públicas?"
        description={`${editionsChangingSlug.length} ${
          editionsChangingSlug.length === 1 ? 'edição terá' : 'edições terão'
        } a URL pública alterada. Links já divulgados deixarão de funcionar.`}
        confirmLabel="Salvar"
      />

      <CaucaoInfoModal isOpen={caucaoInfoOpen} onClose={() => setCaucaoInfoOpen(false)} isSeries />
    </>
  )
}
